import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import classNames from 'clsx'
import colorUtil from 'color'
import i18n from 'i18n-literally'
import memoizeOne from 'memoize-one'
import PropTypes from 'prop-types'
import { pick } from 'ramda'

import {
  ClickAwayListener,
  Grow,
  Paper,
  Popper,
} from '@mui/material'
import { makeStyles } from '@mui/styles'

import getGlobal from '~/src/Lib/getGlobal'
import createLogger from '~/src/Lib/Logging'
import {
  defer,
  EMPTY_OBJECT,
  noop,
  uniqueId,
  useControlledStateV2,
} from '~/src/Lib/Utils'
import ArrowDown from '~/src/UI/Icons/ArrowDown'

import Button from './Button'

const defaultId = 'button-select'
const displayName = 'ButtonSelect'
const logger = createLogger(displayName)

const ownClasses = ['button', 'menu', 'paper']
const pickOwnClasses = memoizeOne(pick(ownClasses))

const useStyles = makeStyles(theme => {
  const mainColor = colorUtil(theme.palette.primary.main)
  return {
    button: {
      '& [class*=MuiButton-endIcon] svg': {
        transition: 'transform 0.25s ease-in-out',
      },
      '@media print': {
        display: 'none'
      }
    },
    disabled: {},
    open: {
      '& [class*=MuiButton-endIcon] svg': {
        transform: 'rotate(180deg)'
      }
    },
    menu: {
      zIndex: 2000,
    },
    paper: {
      overflowY: 'auto',
      backgroundColor: theme.palette.background.secondary,
      '&$dark': {
        backgroundColor: theme.palette.background.paper,
      }
    },
    dark: {},
    text: {
      padding: theme.spacing(0, 2),
      height: '2rem',
    },
    select: {
      height: '3rem',
      padding: theme.spacing(1.5, 2),
      background: mainColor.alpha(0.06).string(),
      borderRadius: theme.shape.borderRadius,
      justifyContent: 'flex-start',
      '& [class*=MuiButton-startIcon]': {
        marginLeft: -4
      },
      '& [class*=MuiButton-endIcon]': {
        flexGrow: 1,
        justifyContent: 'flex-end'
      },
      '&:hover': {
        background: mainColor.alpha(0.12).string(),
      },
      '&:focus': {
        background: mainColor.alpha(0.1).string(),
      },
      '&:active': {
        background: mainColor.alpha(0.16).string(),
      },
      // TODO: do we need these styles?
      '&$rectangular': {
        borderRadius: theme.shape.borderRadius,
        padding: theme.spacing(1, 2),
        height: 'auto'
      },
    },
    rectangular: {
      height: '3rem',
      padding: '15px 35px',
      borderRadius: theme.shape.borderRadius,
      backgroundColor: theme.palette.primary.enabled,
    },
  }
}, { name: displayName })

const getMenulayout = button => {
  if (!button) return null
  const { bottom, top, width } = button.getBoundingClientRect() ?? EMPTY_OBJECT
  const downMaxHeight = getGlobal().innerHeight - bottom - 8
  const upMaxHeight = top - 8
  const vertical = downMaxHeight > 300 || downMaxHeight > upMaxHeight ? 'bottom' : 'top'

  return [vertical, `${vertical === 'bottom' ? downMaxHeight : upMaxHeight}px`, width]
}

const defaultIcon = <ArrowDown />

/**
 * Generic button + empty menu that allows fully customizable menu content
 * @component
 * @composes
 * from: ~/src/UI/Shared/Button
 * name: ButtonComponent
 * @alias ButtonSelect
 * @example
 * import List from '@mui/material/List'
 * import ListItem from '@mui/material/ListItem'
 * {() => {
 *   const [open, setOpen] = React.useState(false)
 *   const [selected, setSelected] = React.useState(null)
 *   const onMenuItemClick = number => setSelected(old => (old === number ? null : number))
 *   const onOpen = () => setOpen(true)
 *   const onClose = () => setOpen(false)
 *   return (
 *     <Box display="flex" alignItems="center" style={{ gap: '1rem' }}>
 *       <ButtonSelect
 *         btnTitle={`${open ? 'Close' : 'Open'} menu`}
 *         open={open}
 *         onOpen={onOpen}
 *         onClose={onClose}
 *       >
 *         <List>
 *           {Array.from({ length: 5 }, (_, index) => (
 *             <ListItem button key={index} onClick={() => onMenuItemClick(index + 1)}>
 *               <T.Bold>Option {index + 1}</T.Bold>
 *             </ListItem>
 *           ))}
 *         </List>
 *       </ButtonSelect>
 *       {selected ? <T.Bold color="primary">selected: {selected}</T.Bold> : null}
 *     </Box>
 *   )
 * }}
 * @named_export ButtonSelectComponent
 */
export const ButtonSelectComponent = ({
  btnTitle = i18n`Open menu`,
  classes: classesProp = EMPTY_OBJECT,
  children,
  dark = false,
  closeOnChildClick = true,
  fullHeight = false,
  id: idProp = defaultId,
  menuLabel = '',
  open = null,
  select = false,
  endIcon = defaultIcon,
  rectangular = false,
  onClose = noop,
  onOpen = noop,
  ...buttonProps
}) => {
  const classes = useStyles({ classes: pickOwnClasses(classesProp) })
  const [anchorEl, setAnchorEl] = useState(null)
  const onOpenRef = useRef(onOpen)
  onOpenRef.current = onOpen
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose
  const menuStateHandler = useCallback(isOpen => {
    if (isOpen) {
      const { current: opener } = onOpenRef
      if (opener?.call) opener()
    } else {
      const { current: closer } = onCloseRef
      if (closer?.call) closer()
    }
  }, [])
  const [menuOpen, setMenuOpen] = useControlledStateV2({
    defaultValue: false,
    prop: open,
    setter: menuStateHandler,
    transform: Boolean,
  })
  const [menuLayout, setMenuLayout] = useState({ vertical: 'bottom', style: { maxHeight: 'auto' } })
  const menuContainer = useRef(getGlobal().document && getGlobal().document?.getElementById('menuRoot'))
  const buttonRef = useRef(null)

  const [menuId, buttonId] = useMemo(() => {
    const prefix = uniqueId(idProp)
    return [`${prefix}-opener`, `${prefix}-menu`]
  }, [idProp])

  const handleClick = useCallback(event => {
    event.stopPropagation()
    const { currentTarget } = event
    if (currentTarget.id !== buttonId) return

    setAnchorEl(oldAnchor => {
      if (!oldAnchor) {
        return currentTarget
      }
      if (oldAnchor !== currentTarget) {
        return oldAnchor
      }
      return null
    })
    setMenuOpen(old => {
      const newState = !old
      if (newState) {
        defer(onOpen, defer.priorities.high)
      } else {
        defer(onClose, defer.priorities.high)
      }
      return newState
    })
  }, [buttonId, setMenuOpen, onOpen, onClose])
  const handleClose = useCallback(({ target }) => {
    const buttonContainsClicked = target.closest(`#${buttonId}`)
    const menuContainsClicked = target.closest(`#${menuId}`)
    const dialogContainsClicked = target.closest('[role=dialog]')
    if (buttonContainsClicked || (menuContainsClicked && !closeOnChildClick) || (
      dialogContainsClicked && !menuContainsClicked
    )) {
      return
    }
    setMenuOpen(isOpen => {
      if (!isOpen) return isOpen

      defer(() => {
        onClose()
        defer(() => setAnchorEl(null), defer.priorities.high)
      }, defer.priorities.high)

      return false
    })
  }, [buttonId, closeOnChildClick, menuId, onClose, setMenuOpen])

  useEffect(() => {
    if (buttonRef.current) {
      setMenuLayout(oldLayout => {
        const [vertical, maxHeight, minWidth] = getMenulayout(buttonRef.current)
        if (vertical !== oldLayout.vertical || maxHeight !== oldLayout.style.maxHeight) {
          return {
            vertical,
            style: fullHeight
              ? { maxHeight, minHeight: maxHeight, minWidth }
              : { maxHeight, minWidth }
          }
        }
        return oldLayout
      })
    }
  }, [fullHeight])

  useEffect(() => {
    if (!menuOpen && anchorEl) {
      setAnchorEl(null)
    }
  }, [menuOpen, anchorEl])

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <div style={{ display: 'contents' }}>
        <Button
          key={idProp}
          ref={buttonRef}
          color="primary"
          endIcon={endIcon}
          {...buttonProps}
          id={buttonId}
          aria-describedby={menuId}
          className={classNames({
            [classes.button]: true,
            [classes.disabled]: buttonProps.disabled,
            [classes.text]: buttonProps.variant === 'text',
            [classes.select]: select,
            [classes.rectangular]: rectangular,
            [classes.open]: endIcon === defaultIcon && (menuOpen || anchorEl),
          })}
          onClick={handleClick}
        >
          {btnTitle}
        </Button>
        <Popper
          anchorEl={anchorEl}
          aria-label={menuLabel || i18n`${btnTitle} menu`}
          className={classes.menu}
          elevation={8}
          container={menuContainer.current}
          id={menuId}
          open={menuOpen}
          placement={`${menuLayout.vertical}-start`}
          role={children?.props?.role === 'menu' ? undefined : 'menu'}
          onClose={handleClose}
          transition
        >
          {({ TransitionProps }) => (
            <Grow {...TransitionProps} style={{ transformOrigin: '0 0 0' }}>
              <Paper
                data-time-series__cursor-exclude
                className={classNames(classes.paper, dark && classes.dark)}
                style={menuLayout.style}
                onClick={handleClose}
              >
                {fullHeight
                  ? React.cloneElement(children, { style: { ...children.props.style, ...menuLayout.style } })
                  : children}
              </Paper>
            </Grow>
          )}
        </Popper>
      </div>
    </ClickAwayListener>
  )
}
ButtonSelectComponent.displayName = displayName

ButtonSelectComponent.propTypes = {
  /** Button label text */
  btnTitle: PropTypes.node,
  /** Content to display in the popup menu */
  children: PropTypes.element.isRequired,
  /** JSS style rules */
  classes: PropTypes.objectOf(PropTypes.string),
  /** toggles on background.dark background color for popup menu */
  dark: PropTypes.bool,
  /** Should the menu close on click? */
  closeOnChildClick: PropTypes.bool,
  /** Should the menu expand to full on-screen height? */
  fullHeight: PropTypes.bool,
  /** If passed, will control the menu open state */
  open: PropTypes.bool,
  rectangular: PropTypes.bool,
  /** test-id attribute */
  'data-testid': PropTypes.string,
  /** id attribute */
  id: PropTypes.string,
  /** value to place in aria-label of menu node */
  menuLabel: PropTypes.node,
  /** Should the opener button be styled like a select? */
  select: PropTypes.bool,
  /** endIcon for opener button */
  endIcon: PropTypes.node,
  /** Called when ButtonSelect thinks it should close */
  onClose: PropTypes.func,
  /** Called when ButtonSelect thinks it should open */
  onOpen: PropTypes.func,
}

export default React.memo(ButtonSelectComponent)
